<template>
  <div>
    <div v-if="$userIsGraduate && placementStartComponentIsSignedOff" class="alert alert-info d-flex justify-content-between align-items-center">
      <div>
        <strong>Notice:</strong> This plan has been approved. Any changes will require resubmission for approval by your manager.
      </div>
      <ladda-button
        v-if="cannotEditPlacement"
        type="button"
        class="btn btn-info ml-3"
        :validate="false"
        :click="confirmBeforeRequestEditAccess"
        :failed="failed"
        :done="requestSubmitted"
      >
        Request Edit Access
      </ladda-button>
    </div><!-- /.alert -->

    <collapse-card name="directions">
      <template v-slot:title>
        <font-awesome :icon="['far', 'info-circle']" class="text-muted mr-2"/>Directions
      </template>

      <template v-if="$userIsGraduate">
        <p class="strong">Define 3-4 performance objectives for this placement (maximum of 4).</p>

        <p>These enable you to set clear expectations around your performance and priorities throughout your 6-month placement. Include:</p>

        <ul>
          <li>What you will do – objectives, targets, time and measures</li>
          <li>How you will do it – capabilities demonstrated</li>
        </ul>

        <p
          class="strong"
        >When creating your performance objectives you are encouraged to reference the following information:</p>
        <ul>
          <li>Graduate role description</li>
          <li>Team objectives</li>
          <li>Your department’s corporate plan and/or business plans</li>
          <li>Discussions with your manager</li>
          <li>Values and Code of Conduct</li>
          <li>Graduate workbook (Diploma course)</li>
        </ul>

        <p class="strong">When creating your performance objectives follow the SMART principles.</p>

        <img
          src="~assets/img/smart-graphic.jpg"
          alt="Graphic outlinging SMART principles"
          longdesc="#longdesc-smart-graphic"
          class="mx-0"
        >

        <div id="longdesc-smart-graphic" class="sr-only">
          <h2>Specific</h2>
          <p>Define what you will accomplish with no unclear language. What, when, where and how.</p>

          <h2>Measurable</h2>
          <p>Can you track the progress and measure the outcome? Provide metrics or targets.</p>

          <h2>Attainable</h2>
          <p>Is it reasonable to be accomplished and within expectations of role?</p>

          <h2>Relevant</h2>
          <p>Is it consistent with your other objectives, meet your needs and relate to your wider job function?</p>

          <h2>Timely</h2>
          <p>Define a time limit. This will establish a sense of urgency and promote better time management.</p>
        </div><!-- /#longdesc-smart-graphic -->
      </template>

      <template v-else-if="$userIsManager || $userIsCoordinator">
        <p>Performance objectives enable a graduate to set clear expectations around their performance and priorities throughout the 6-month placement. They should include:</p>
        <ul>
          <li>What they will do – objectives, targets, time and measures</li>
          <li>How they will do it – capabilities demonstrated</li>
        </ul>
        <p>After reviewing your graduate’s performance and development objectives you will have an opportunity to advise the graduate if any changes are required, or you can approve and sign off the Start of Placement Plan.</p>
      </template>
    </collapse-card>

    <collapse-card v-if="$userIsGraduate" name="examples">
      <template v-slot:title>Examples</template>

      <p>
        <strong>Performance Objective</strong>
        <br>Research existing policies and guidelines relating to Flexible Working Arrangements in the public and private sector.
      </p>

      <p class="mb-0">
        <strong>Key Milestone</strong>
        <br>Prepare a project scope and project schedule.
      </p>
    </collapse-card>

    <placements-start-component-capability-data-card v-if="$userIsGraduate"/>

    <h2 class="mt-5">Performance Objectives</h2>

    <template v-if="canEditPlacement">
      <placements-start-component-performance-item
        v-for="(objective, index) in objectives"
        :key="index"
        :objective-index="index"
        :objective="objective"
      />
    </template>
    <template v-else-if="$userIsManager || $userIsCoordinator || cannotEditPlacement">
      <placements-performance-item-read-only
        v-for="objective in objectives"
        :key="objective.id"
        :objective="objective"
      />
    </template>

    <template v-if="objectives.length < 4 && canEditPlacement">
      <div class="decorative-divider"></div>

      <button type="button" class="btn btn-sm btn-outline-primary" @click="addBlankObjective">
        <font-awesome :icon="['far', 'plus']"/>
        Add Performance Objective
      </button>
    </template>

    <div class="save-actions">
      <div
        class="alert alert-warning"
        v-if="objectives.length === 4 && $userIsGraduate"
      >You have reached the maximum of 4 performance objectives.</div>

      <div class="alert alert-danger" v-if="otherErrors.length > 0">
        <font-awesome :icon="['fas', 'times']" class="float-right" @click="resetValidation"/>

        <p v-for="(error, index) in otherErrors" :key="`error-${index}`">{{ error }}</p>
      </div>

      <div class="text-right" v-if="canEditPlacement">
        <button
          @click="discard"
          class="btn btn-link btn-discard mr-2"
        >Discard &amp; Exit</button>
        <ladda-button
          type="button"
          class="btn btn-outline-dark mr-2"
          :validate="false"
          :arguments="[{ draft: true, objectives: objectivesWithoutLastMilestone }]"
          :click="storePerformanceObjectives"
          :done="draftSaved"
          :failed="failed"
        >Save &amp; Exit</ladda-button>
        <ladda-button
          type="button"
          class="btn btn-success"
          :validate="false"
          :arguments="[{ draft: false, objectives: objectivesWithoutLastMilestone }]"
          :click="storePerformanceObjectives"
          :failed="failed"
          :done="objectivesCompleted"
        >Next</ladda-button>
      </div>
      <div v-else-if="$userIsManager || $userIsCoordinator" class="text-right">
        <router-link :to="{ name: 'graduates.placements.single', params: { placementId: placement.id, graduateId: placement.graduate_id } }" class="btn btn-link mr-2">
          Exit
        </router-link>
        <router-link :to="{ name: 'placements.single.start.manager.development', params: { placementId: placement.id, graduateId: placement.graduate_id } }" class="btn btn-success" active-class="">
          Next
        </router-link>
      </div>
    </div><!-- /.save-actions -->
  </div>
</template>

<style scoped>
svg.float-right {
  cursor: pointer;
}
</style>

<script>
import { forIn, each, cloneDeep, map } from 'lodash'
import { alert } from 'utils'
import { placementPerformanceObjectiveComputed, placementPerformanceObjectiveMethods, placementMethods, placementComputed } from 'vx/helpers'
import { helpers } from 'mixins/placements'
import CollapseCard from 'components/CollapseCard'
import PlacementsStartComponentCapabilityDataCard from './PlacementsStartComponentCapabilityDataCard'
import PlacementsStartComponentPerformanceItem from './PlacementsStartComponentPerformanceItem'
import PlacementsPerformanceItemReadOnly from './PlacementsPerformanceItemReadOnly'

export function populateObjectiveServerErrors (response) {
  this.otherErrors = []

  forIn(response.data.errors, (errors, name) => {
    let scope
    let field

    if (name.indexOf('.') !== -1) {
      scope = name.split('.').slice(0, -1).join('.')

      field = this.$validator.fields.find({ name: name.split('.').pop(), scope })
    } else {
      scope = null

      field = this.$validator.fields.find({ name })
    }

    this.otherErrors.push(errors[errors.length - 1])

    if (field === undefined) {
      return
    }

    field.setFlags({ invalid: true, valid: false, validated: true })

    each(errors, (msg) => {
      this.errors.add({ field: field.name, id: field.id, msg, scope })
    })
  })
}

export default {
  mixins: [helpers],

  components: {
    CollapseCard,
    PlacementsStartComponentPerformanceItem,
    PlacementsPerformanceItemReadOnly,
    PlacementsStartComponentCapabilityDataCard
  },

  data () {
    return {
      otherErrors: []
    }
  },

  computed: {
    ...placementComputed,
    ...placementPerformanceObjectiveComputed,

    objectivesWithoutLastMilestone () {
      return map(cloneDeep(this.objectives), (objective) => {
        objective.milestones.pop()

        return objective
      })
    },

    canEditPlacement () {
      return this.$userIsGraduate && !this.placementStartComponentIsLocked
    },

    cannotEditPlacement () {
      return this.$userIsGraduate && this.placementStartComponentIsLocked
    }
  },

  async created () {
    await this.getPerformanceObjectives()
  },

  watch: {
    objectives: {
      handler () {
        if (this.objectives.length === 0) {
          this.addBlankObjective()
        }

        if (this.errors.any()) {
          this.$validator.reset()
        }
      },
      deep: true
    }
  },

  methods: {
    ...placementPerformanceObjectiveMethods,
    ...placementMethods,

    resetValidation () {
      this.otherErrors = []

      this.$validator.reset()
    },

    async discard () {
      if (
        !await alert.confirm({
          title: 'Discard Changes?',
          text: 'Are you sure you want to discard your changes.'
        })
      ) {
        return
      }

      this.$router.push({ name: 'placements.single' })
    },

    failed (error) {
      if (error.response && error.response.status === 422) {
        this.populateObjectiveServerErrors(error.response)
      }
    },

    populateObjectiveServerErrors,

    draftSaved () {
      this.$noty.success('Performance objectives have been saved successfully.')

      this.$router.push({ name: 'placements.single' })
    },

    async objectivesCompleted () {
      // If we have just completed the objectives but the start component hasn't been updated
      // we'll fire off a request to refetch the placement.
      if (
        this.placementStartComponent.status === null ||
        this.placementStartComponent.performance_objectives_completed === false
      ) {
        await this.getPlacement({ placement: this.placement.id, graduate: this.placement.graduate_id })
      }

      this.$router.push({ name: 'placements.single.start.development' })
    },

    async confirmBeforeRequestEditAccess () {
      if (
        !await alert.confirm({
          title: 'Request Edit Access',
          text: 'Are you sure you want to submit your request to edit the placement plan?'
        })
      ) {
        return
      }

      let payload = {
        start_component_id: this.placement.start_component.id,
        manager_id: this.placement.manager_id,
        agency_id: this.placement.agency_id ? this.placement.agency_id : null
      }
      await this.requestEditAccess(payload)
    },

    requestSubmitted () {
      this.$noty.success('Request was submitted.')
    }
  }
}
</script>
